/**
 * useStorylines — the storyline skeleton for the Assets Menu tree ("View by
 * Storyline", storyline-design.md M2). Fetches ListStorylines (all industries)
 * so the Menu tree can show every storyline node — including EMPTY ones (0 linked
 * assets), which asset-derived reverse-lookup from storylineLinks can't produce.
 *
 * Resilient by design (same idiom as useTreeUniverse): if the call fails or no
 * client is set (e.g. a unit test that only cares about asset-derived branches),
 * it returns an empty list and never throws — the Menu tree then shows only the
 * per-industry Unassigned buckets. A `reloadKey` bump re-fetches (after create /
 * edit / delete mutate the set).
 */
import { useEffect, useState } from 'react';
import { storylineClient } from '../../../client/storylineClient';
import type { StorylineSummary } from '../../../client/IvtHubClient';

export function useStorylines(reloadKey = 0): StorylineSummary[] {
  const [storylines, setStorylines] = useState<StorylineSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    // getIvtClient() can throw synchronously when no client is set (unit tests);
    // wrap in a resolved promise so this hook never throws — empty list fallback.
    Promise.resolve()
      .then(() => storylineClient.list())
      .then((items) => {
        if (!cancelled) setStorylines(items);
      })
      .catch(() => {
        if (!cancelled) setStorylines([]);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return storylines;
}
